/**
 * Composer draft autosave — everything the post composer holds beyond the
 * cross-tool create-flow store (per-network caption overrides, alt text, first
 * comment, link, timezone, publish options, probed media metadata), persisted
 * to localStorage so a reload / crash never loses work. The caption, hashtags,
 * targets and media are mirrored here too (`snapshot`) so the composer can
 * offer "Restored your unsaved draft" after a reload.
 */
import { createAppStore } from "@plerity/dashboard-shared/stores";
import { persist } from "zustand/middleware";
import { type ComposerBioLink, DEFAULT_BIO_LINK } from "../lib/composerBioLink";
import { DEFAULT_PLATFORM_OPTIONS, type PlatformOptionsState } from "../lib/platformOptions";
import type { MediaMeta } from "../lib/platformRules";
import type { AttachedMedia, Platform } from "../types/social";

export interface ComposerSnapshot {
  caption: string;
  hashtags: string;
  platforms: Array<{ platform: string; account_id: string }>;
  media: AttachedMedia[];
  scheduleDate: string;
  scheduleTime: string;
  /** Content idea the draft was started from ("Write it now") → metadata.content_idea_id. */
  contentIdeaId?: string | null;
}

/**
 * A persisted options object from an older build may lack newer keys (or be
 * junk) — every key falls back to its default so a restore never yields
 * `undefined` controls.
 */
export function normalizePlatformOptions(raw: unknown): PlatformOptionsState {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ...DEFAULT_PLATFORM_OPTIONS };
  const value = raw as Record<string, unknown>;
  const out: Record<string, unknown> = { ...DEFAULT_PLATFORM_OPTIONS };
  for (const [key, fallback] of Object.entries(DEFAULT_PLATFORM_OPTIONS)) {
    const v = value[key];
    if (Array.isArray(fallback)) {
      if (Array.isArray(v) && v.every((x) => typeof x === "string")) out[key] = v;
    } else if (typeof v === typeof fallback) {
      out[key] = v;
    }
  }
  return out as unknown as PlatformOptionsState;
}

function normalizeBioLink(raw: unknown): ComposerBioLink {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ...DEFAULT_BIO_LINK };
  const v = raw as Record<string, unknown>;
  return {
    enabled: v.enabled === true,
    pageId: typeof v.pageId === "string" ? v.pageId : null,
    url: typeof v.url === "string" ? v.url : "",
    label: typeof v.label === "string" ? v.label : "",
  };
}

interface ComposerDraftState {
  overrides: Partial<Record<Platform, string>>;
  /** Alt text keyed by media URL (survives reordering). */
  altText: Record<string, string>;
  /** Probed media facts keyed by URL (dimensions, size, duration). */
  mediaMeta: Record<string, Omit<MediaMeta, "url">>;
  firstComment: string;
  linkUrl: string;
  /** Display-only post title (`metadata.title`, 2026-10-10) — shown to the
   * team and the client in Approvals/post lists, never published to any
   * network. Edit mode seeds it from the post; clearing it on an existing
   * post sends an explicit clear (see useComposerSubmit buildPostUpdateBody). */
  postTitle: string;
  /** Timezone chosen for THIS draft; null = Settings timezone (or the browser zone). */
  timezone: string | null;
  tiktokPublishMode: "direct" | "draft";
  pinterestBoardId: string;
  /** Facebook video as a Page video (feed) or a Reel. */
  facebookVideoFormat: "feed" | "reel";
  /** Facebook video title (optional). */
  videoTitle: string;
  /** Instagram Reel: also show it in the main profile grid (share_to_feed). */
  instagramShareToFeed: boolean;
  /** Instagram Reel cover image (hosted URL) — wins over the cover frame. */
  videoCoverUrl: string;
  /** Instagram Reel cover frame, seconds into the video (null = Instagram's default). */
  videoCoverSeconds: number | null;
  /** UTM-tag links at publish time (this draft). */
  utmEnabled: boolean;
  /** utm_campaign for this draft ("" = derived from the caption). */
  utmCampaign: string;
  /** The last UTM choice — every new draft starts from it (survives reset). */
  utmPreferred: boolean;
  /** True once the org-wide UTM default (GET /api/org-settings) has been
   *  applied (or found off) once in this browser — never re-applies over a
   *  later explicit per-post choice. */
  utmDefaultSeeded: boolean;
  /**
   * Per-platform publish options (lib/platformOptions — sponsorship, IG story /
   * trial reel / collaborators, Threads topic / poll / replies, TikTok privacy +
   * interactions + disclosure, GBP event / offer / button). Persisted so a
   * reload keeps them; cleared with the rest of the draft on a successful save.
   */
  platformOptions: PlatformOptionsState;
  /** "Add to link in bio" (lib/composerBioLink) — synced to the bio page after the post saves. */
  bioLink: ComposerBioLink;
  /** The post whose saved bio link was loaded into `bioLink` (edit mode seeds once per post). */
  bioLinkSeededFor: string | null;
  snapshot: ComposerSnapshot | null;
  savedAt: number | null;

  setPlatformOptions: (options: PlatformOptionsState) => void;
  setBioLink: (patch: Partial<ComposerBioLink>) => void;
  seedBioLink: (postId: string, link: ComposerBioLink) => void;
  setOverride: (platform: Platform, text: string | null) => void;
  setAltText: (url: string, text: string) => void;
  setMediaMeta: (url: string, meta: Omit<MediaMeta, "url">) => void;
  setFirstComment: (text: string) => void;
  setLinkUrl: (url: string) => void;
  setPostTitle: (title: string) => void;
  setTimezone: (tz: string) => void;
  setTiktokPublishMode: (mode: "direct" | "draft") => void;
  setPinterestBoardId: (id: string) => void;
  setFacebookVideoFormat: (format: "feed" | "reel") => void;
  setVideoTitle: (title: string) => void;
  setInstagramShareToFeed: (share: boolean) => void;
  setVideoCoverUrl: (url: string) => void;
  setVideoCoverSeconds: (seconds: number | null) => void;
  setUtmEnabled: (enabled: boolean) => void;
  setUtmCampaign: (campaign: string) => void;
  /** Apply the org-wide UTM default once; a no-op on every later call. */
  seedUtmDefault: (enabled: boolean, campaign: string) => void;
  saveSnapshot: (snapshot: ComposerSnapshot) => void;
  reset: () => void;
}

const initial = {
  overrides: {} as Partial<Record<Platform, string>>,
  altText: {} as Record<string, string>,
  mediaMeta: {} as Record<string, Omit<MediaMeta, "url">>,
  firstComment: "",
  linkUrl: "",
  postTitle: "",
  timezone: null as string | null,
  tiktokPublishMode: "draft" as "direct" | "draft",
  pinterestBoardId: "",
  facebookVideoFormat: "feed" as "feed" | "reel",
  videoTitle: "",
  instagramShareToFeed: true,
  videoCoverUrl: "",
  videoCoverSeconds: null as number | null,
  utmEnabled: false,
  utmCampaign: "",
  utmPreferred: false,
  utmDefaultSeeded: false,
  platformOptions: DEFAULT_PLATFORM_OPTIONS as PlatformOptionsState,
  bioLink: DEFAULT_BIO_LINK as ComposerBioLink,
  bioLinkSeededFor: null as string | null,
  snapshot: null as ComposerSnapshot | null,
  savedAt: null as number | null,
};

export const useComposerDraftStore = createAppStore<ComposerDraftState>(
  "social-composer-draft",
  persist(
    (set) => ({
      ...initial,
      setPlatformOptions: (platformOptions) => set({ platformOptions }),
      setBioLink: (patch) => set((s) => ({ bioLink: { ...s.bioLink, ...patch } })),
      seedBioLink: (postId, bioLink) => set({ bioLink, bioLinkSeededFor: postId }),
      setOverride: (platform, text) =>
        set((s) => {
          const next = { ...s.overrides };
          if (text === null) delete next[platform];
          else next[platform] = text;
          return { overrides: next };
        }),
      setAltText: (url, text) => set((s) => ({ altText: { ...s.altText, [url]: text } })),
      setMediaMeta: (url, meta) => set((s) => ({ mediaMeta: { ...s.mediaMeta, [url]: meta } })),
      setFirstComment: (firstComment) => set({ firstComment }),
      setLinkUrl: (linkUrl) => set({ linkUrl }),
      setPostTitle: (postTitle) => set({ postTitle }),
      setTimezone: (timezone) => set({ timezone }),
      setTiktokPublishMode: (tiktokPublishMode) => set({ tiktokPublishMode }),
      setPinterestBoardId: (pinterestBoardId) => set({ pinterestBoardId }),
      setFacebookVideoFormat: (facebookVideoFormat) => set({ facebookVideoFormat }),
      setVideoTitle: (videoTitle) => set({ videoTitle }),
      setInstagramShareToFeed: (instagramShareToFeed) => set({ instagramShareToFeed }),
      setVideoCoverUrl: (videoCoverUrl) => set({ videoCoverUrl }),
      setVideoCoverSeconds: (videoCoverSeconds) => set({ videoCoverSeconds }),
      setUtmEnabled: (utmEnabled) => set({ utmEnabled, utmPreferred: utmEnabled }),
      setUtmCampaign: (utmCampaign) => set({ utmCampaign }),
      seedUtmDefault: (enabled, campaign) =>
        set((s) =>
          s.utmDefaultSeeded
            ? {}
            : enabled
              ? { utmDefaultSeeded: true, utmEnabled: true, utmCampaign: campaign || s.utmCampaign, utmPreferred: true }
              : { utmDefaultSeeded: true }
        ),
      saveSnapshot: (snapshot) => set({ snapshot, savedAt: Date.now() }),
      // A per-post timezone choice is draft content: each new post starts from
      // the Settings timezone (general.timezone) again. The UTM preference is
      // not draft content — a new draft starts tagged if the last one was.
      // `utmDefaultSeeded` survives too — the org default only ever applies
      // once per browser, not once per draft.
      reset: () =>
        set((s) => ({
          ...initial,
          utmEnabled: s.utmPreferred,
          utmPreferred: s.utmPreferred,
          utmDefaultSeeded: s.utmDefaultSeeded,
        })),
    }),
    {
      name: "social-composer-draft",
      // Probed metadata of inline (data:) media would bloat localStorage; it is
      // re-probed on demand, so only hosted-URL entries persist.
      partialize: (s) => ({
        overrides: s.overrides,
        altText: Object.fromEntries(Object.entries(s.altText).filter(([url]) => !url.startsWith("data:"))),
        mediaMeta: Object.fromEntries(Object.entries(s.mediaMeta).filter(([url]) => !url.startsWith("data:"))),
        firstComment: s.firstComment,
        linkUrl: s.linkUrl,
        postTitle: s.postTitle,
        timezone: s.timezone,
        tiktokPublishMode: s.tiktokPublishMode,
        pinterestBoardId: s.pinterestBoardId,
        facebookVideoFormat: s.facebookVideoFormat,
        videoTitle: s.videoTitle,
        instagramShareToFeed: s.instagramShareToFeed,
        videoCoverUrl: s.videoCoverUrl.startsWith("data:") ? "" : s.videoCoverUrl,
        videoCoverSeconds: s.videoCoverSeconds,
        utmEnabled: s.utmEnabled,
        utmCampaign: s.utmCampaign,
        utmPreferred: s.utmPreferred,
        utmDefaultSeeded: s.utmDefaultSeeded,
        platformOptions: s.platformOptions,
        bioLink: s.bioLink,
        bioLinkSeededFor: s.bioLinkSeededFor,
        snapshot: s.snapshot
          ? { ...s.snapshot, media: s.snapshot.media.filter((m) => !m.url.startsWith("data:")) }
          : null,
        savedAt: s.savedAt,
      }),
      // Stored drafts from an older build: fill missing option keys with defaults.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<ComposerDraftState>;
        return {
          ...current,
          ...p,
          platformOptions: normalizePlatformOptions(p.platformOptions),
          bioLink: normalizeBioLink(p.bioLink),
        };
      },
    }
  )
);
